import { action, computed, makeObservable, runInAction } from 'mobx';

import { AppNotificationType } from 'shared/entities/appNotifications';
import { apiUrls } from 'shared/entities/domain';
import {
  IQuickReplyTemplate,
  mapQuickReplyTemplateErrorCodeToMessage,
  QuickReplyTemplateData,
  QuickReplyTemplateErrorCode,
  QuickReplyTemplateServer,
  quickReplyTemplateToListItem,
  normalizeQuickReplyTemplate
} from 'shared/entities/quickReplyTemplate';
import { IRootStore } from 'shared/entities/store/rootStore';
import {
  IQuickReplyTemplatesStore,
  QuickReplyListPlaceholderKind
} from 'shared/entities/store/quickReplyTemplatesStore';
import { LoadingStage } from 'shared/entities/meta';
import NewListModel from 'shared/models/NewListModel';
import { FieldModel } from 'shared/models/form';
import { LoadingStageModel } from 'shared/models/loadingStage';
import filterBySearchQuery from 'shared/utils/filterBySearchQuery';

const buildInitTemplates = (): NewListModel<IQuickReplyTemplate> =>
  new NewListModel<IQuickReplyTemplate>({
    keys: [],
    entities: new Map(),
    disabledLoadMore: true
  });

/**
 * Локальный store шаблонов быстрых ответов.
 * Страница настроек и чаты создают независимые экземпляры через provider.
 */
export default class QuickReplyTemplatesStore implements IQuickReplyTemplatesStore {
  readonly templates: NewListModel<IQuickReplyTemplate> = buildInitTemplates();

  readonly searchQuery = new FieldModel<string>('');

  readonly editingStage = new LoadingStageModel();
  readonly removingStage = new LoadingStageModel();

  readonly rootStore: IRootStore;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;

    makeObservable(this, {
      silentLoadingStage: computed,
      hasLoadedTemplates: computed,
      hasLoadError: computed,
      isCatalogEmpty: computed,
      load: action,
      create: action,
      update: action,
      delete: action,
      reset: action
    });
  }

  get canBeLoaded(): boolean {
    return this.rootStore.initialized && this.rootStore.userStore.isAuthorized;
  }

  get silentLoadingStage(): LoadingStage {
    return !this.templates.isInitialLoad &&
      this.templates.loadingStage.isLoading
      ? LoadingStage.SUCCESS
      : this.templates.loadingStage.value;
  }

  get hasLoadedTemplates(): boolean {
    return !this.templates.isInitialLoad;
  }

  get hasLoadError(): boolean {
    return this.templates.loadingStage.value === LoadingStage.ERROR;
  }

  get isCatalogEmpty(): boolean {
    return this.templates.items.length === 0;
  }

  filterTemplates = (query: string): IQuickReplyTemplate[] =>
    filterBySearchQuery(this.templates.items, query, ['name', 'body']);

  load = async (): Promise<BaseResponse> => {
    if (!this.canBeLoaded || this.templates.loadingStage.isLoading) {
      return { isError: true };
    }

    this.templates.loadingStage.loading();

    const response = await this.rootStore.networkStore.api<{
      templates: QuickReplyTemplateServer[];
    }>(apiUrls.QUICK_REPLY_TEMPLATES_LIST, {
      method: 'GET',
      showUnexpectedError: false
    });

    runInAction(() => {
      if (response.isError || !response.data) {
        this.templates.loadingStage.error();

        // refetch: каталог уже был, Stub нет — нужен тост вместо unknown
        if (this.hasLoadedTemplates) {
          this.rootStore.appNotificationsStore.open({
            type: AppNotificationType.error,
            title: (t) =>
              t('right.quickReplySelector.refetchError', {
                ns: 'conversationsPage'
              })
          });
        }

        return;
      }

      this.templates.fillByRawData({
        raw: response.data.templates ?? [],
        normalizer: quickReplyTemplateToListItem,
        initial: true
      });
      this.templates.isInitialLoadModel.changeValue(false);
      this.templates.loadingStage.success();
    });

    return response;
  };

  async create(
    data: QuickReplyTemplateData
  ): Promise<
    BaseResponse<
      IQuickReplyTemplate,
      { errorCode: QuickReplyTemplateErrorCode | null }
    >
  > {
    const name = data.name.trim();
    const body = data.body.trim();

    if (!name || !body || this.templates.creatingStage.isLoading) {
      return { isError: true, data: { errorCode: null } };
    }

    this.templates.creatingStage.loading();

    const response = await this.rootStore.networkStore.api<
      {
        template: QuickReplyTemplateServer;
      },
      QuickReplyTemplateErrorCode
    >(apiUrls.QUICK_REPLY_TEMPLATES_CREATE, {
      method: 'POST',
      data: { name, body },
      errorsMap: mapQuickReplyTemplateErrorCodeToMessage
    });

    if (response.isError || !response.data) {
      this.templates.creatingStage.error();

      return {
        isError: true,
        data: {
          errorCode: this.extractErrorCode(response)
        }
      };
    }

    const entity = normalizeQuickReplyTemplate(response.data.template);

    runInAction(() => {
      if (this.templates.getEntity(entity.id)) {
        this.templates.updateEntity(entity.id, entity);
        this.templates.toStart(entity.id);
      } else {
        this.templates.addEntity({
          entity,
          key: entity.id,
          start: true
        });
      }

      this.templates.creatingStage.success();
    });

    this.notifyCreateSuccess();

    return { isError: false, data: entity };
  }

  async update(
    id: string,
    data: QuickReplyTemplateData
  ): Promise<
    BaseResponse<
      IQuickReplyTemplate | null,
      { errorCode: QuickReplyTemplateErrorCode | null }
    >
  > {
    const name = data.name.trim();
    const body = data.body.trim();

    if (!id || !name || !body || this.editingStage.isLoading) {
      return { isError: true, data: { errorCode: null } };
    }

    this.editingStage.loading();

    const response = await this.rootStore.networkStore.api<
      {
        template: QuickReplyTemplateServer;
      },
      QuickReplyTemplateErrorCode
    >(apiUrls.QUICK_REPLY_TEMPLATES_UPDATE, {
      method: 'POST',
      data: { template_id: id, name, body },
      errorsMap: mapQuickReplyTemplateErrorCodeToMessage,
      expectedErrorCodes: [QuickReplyTemplateErrorCode.templateNotFound]
    });

    if (response.isError || !response.data) {
      const errorCode = this.extractErrorCode(response);

      runInAction(() => {
        if (errorCode === QuickReplyTemplateErrorCode.templateNotFound) {
          this.templates.removeEntity(id);
        }
        this.editingStage.error();
      });

      return {
        isError: true,
        data: {
          errorCode
        }
      };
    }

    const entity = normalizeQuickReplyTemplate(response.data.template);

    runInAction(() => {
      if (this.templates.getEntity(id)) {
        this.templates.updateEntity(id, entity);
        this.templates.toStart(id);
      } else {
        this.templates.addEntity({
          entity,
          key: entity.id,
          start: true
        });
      }
      this.editingStage.success();
    });

    this.notifyUpdateSuccess();

    return { isError: false, data: entity };
  }

  async delete(
    id: string
  ): Promise<
    BaseResponse<null, { errorCode: QuickReplyTemplateErrorCode | null }>
  > {
    if (!id || this.removingStage.isLoading) {
      return { isError: true, data: { errorCode: null } };
    }

    this.removingStage.loading();

    const response = await this.rootStore.networkStore.api<
      Record<string, any>,
      QuickReplyTemplateErrorCode
    >(apiUrls.QUICK_REPLY_TEMPLATES_DELETE, {
      method: 'POST',
      data: { template_id: id },
      errorsMap: mapQuickReplyTemplateErrorCodeToMessage
    });

    const errorCode = this.extractErrorCode(response);

    if (!response.isError) {
      runInAction(() => {
        this.templates.removeEntity(id);
        this.removingStage.success();
      });

      this.notifyDeleteSuccess();

      return { isError: false, data: null };
    }

    if (errorCode === QuickReplyTemplateErrorCode.templateNotFound) {
      runInAction(() => {
        this.templates.removeEntity(id);
        this.removingStage.error();
      });

      return { isError: true, data: { errorCode } };
    }

    this.removingStage.error();

    return { isError: true, data: { errorCode } };
  }

  resolvePlaceholder(
    filteredItemsCount: number
  ): QuickReplyListPlaceholderKind | null {
    const isLoading = this.templates.loadingStage.isLoading;

    if (!this.hasLoadedTemplates && isLoading) {
      return QuickReplyListPlaceholderKind.initialLoading;
    }

    if (!this.hasLoadedTemplates && !isLoading && this.hasLoadError) {
      return QuickReplyListPlaceholderKind.initialError;
    }

    if (this.hasLoadedTemplates && this.isCatalogEmpty) {
      return QuickReplyListPlaceholderKind.emptyCatalog;
    }

    if (
      this.hasLoadedTemplates &&
      !this.isCatalogEmpty &&
      filteredItemsCount === 0
    ) {
      return QuickReplyListPlaceholderKind.emptySearch;
    }

    return null;
  }

  private notifyCreateSuccess(): void {
    this.rootStore.appNotificationsStore.open({
      type: AppNotificationType.success,
      title: (t) =>
        t('chats.quickReplyTemplates.form.createSuccess', {
          ns: 'projectSettingsPage'
        })
    });
  }

  private notifyUpdateSuccess(): void {
    this.rootStore.appNotificationsStore.open({
      type: AppNotificationType.success,
      title: (t) =>
        t('chats.quickReplyTemplates.form.updateSuccess', {
          ns: 'projectSettingsPage'
        })
    });
  }

  private notifyDeleteSuccess(): void {
    this.rootStore.appNotificationsStore.open({
      type: AppNotificationType.success,
      title: (t) =>
        t('chats.quickReplyTemplates.removeModal.success', {
          ns: 'projectSettingsPage'
        })
    });
  }

  private extractErrorCode(response: {
    isError: boolean;
    data?: unknown;
  }): QuickReplyTemplateErrorCode | null {
    if (!response.isError) {
      return null;
    }

    return (
      (response.data as { code?: QuickReplyTemplateErrorCode } | null)?.code ??
      null
    );
  }

  reset(): void {
    this.templates.reset();
    this.searchQuery.reset();
    this.editingStage.reset();
    this.removingStage.reset();
  }
}
